/** Historical Imports words for the run register's server reads, its register totals and term coverage. */
import { historyWords } from "@/components/history/history-copy";
import { importsWords } from "./imports-copy";
import type { RunsFilter } from "./run-register";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const day = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: "long" });

export const registerWords = {
  // Run register
  readingRuns: "Reading runs",
  noAppliedRunsYet: "No applied runs yet",
  noDryRunsYet: "No dry runs yet",
  olderRuns: "Older runs",
  notReadYet: "Not read yet",
  readOlderRuns: "Read older runs",
  readingOlderRuns: "Reading older runs",
  olderRunsFailed: "Older runs could not be read",
  tryAgain: "Try again",
  // Register totals
  totals: "Register totals",
  totalsJob: "Read what every recorded run applied, wrote and refused, whatever the register's filter",
  // Term coverage
  coverage: "Term coverage",
  coverageJob: "See which closed terms are fully recorded and what each one still lacks",
  readCoverage: "Read term coverage",
  complete: "Complete",
  everyTermComplete: "Every closed term complete",
  noClosedTerms: "No closed terms recorded yet",
  noClosedTermsSub: "Bring them in with Bring in closed-term records above.",
  noSectionsRecorded: "No sections recorded",
  noAppliedRun: "No applied run",
} as const;

export const registerText = {
  /** An empty page says what it was read for: a narrowed read found no match; an unnarrowed one has none of that mode yet. */
  emptyWord: (runs: RunsFilter, narrowed: boolean) => narrowed ? importsWords.noRunMatches
    : runs === "apply" ? registerWords.noAppliedRunsYet : runs === "dry_run" ? registerWords.noDryRunsYet : historyWords.noImportRunsYet,
  runsShown: (n: number, more: boolean) => `${plural(n, "run", "runs")} shown${more ? " · older runs not read yet" : ""}`,
  group: (startedAt: string) => day(startedAt),
  // Register totals
  totalsDetail: (lastAppliedAt: string | null) =>
    `Every recorded run, whatever the filter · ${lastAppliedAt ? `last applied ${day(lastAppliedAt)}` : "no run applied yet"}`,
  appliedRuns: (n: number) => n === 1 ? "applied run" : "applied runs",
  recordsWritten: (n: number) => n === 1 ? "record written" : "records written",
  refusedRows: (n: number) => n === 1 ? "refused row" : "refused rows",
  dryRuns: (n: number) => n === 1 ? "dry run" : "dry runs",
  // Term coverage
  termsComplete: (total: number) => `of ${plural(total, "closed term", "closed terms")} complete`,
  withoutOutcome: (n: number) => n === 1 ? "enrollment without a certified outcome" : "enrollments without a certified outcome",
  coverageDetail: (complete: number, total: number) => `${complete} of ${plural(total, "closed term", "closed terms")} complete`,
  termsIncomplete: (n: number) => `${plural(n, "term", "terms")} incomplete`,
  gap: (kind: "instructor" | "enrollment" | "outcome", n: number) => kind === "instructor"
    ? `${plural(n, "section", "sections")} without an instructor`
    : kind === "enrollment"
      ? `${plural(n, "section", "sections")} without an enrollment`
      : `${plural(n, "enrollment", "enrollments")} without a certified outcome`,
  termFacts: (t: { imported_sections: number; sections: number; enrollments: number; certified_outcomes: number; last_run_at: string | null }) =>
    [`${t.imported_sections} of ${plural(t.sections, "section", "sections")} imported`, plural(t.enrollments, "enrollment", "enrollments"),
      plural(t.certified_outcomes, "certified outcome", "certified outcomes"),
      t.last_run_at ? `last applied run ${day(t.last_run_at)}` : registerWords.noAppliedRun].join(" · "),
} as const;
